/* ============================================================================
 * COLLECTION CONTROLLER
 * ----------------------------------------------------------------------------
 * Powers the Shopify collection page:
 *   - Filter / sort without a full page reload (uses Shopify's Section
 *     Rendering API, which gives back HTML for just one section).
 *   - Updates the browser URL with history.pushState so the back button
 *     works and shared links keep filters.
 *   - Two-thumb price slider, accordion filter groups (slide animation),
 *     load more pagination, and a clear-all-filters button.
 *
 * Reading guide:
 *   1. The class is built like a small app: constructor → init → handlers.
 *   2. All DOM events are wired up in `bindEvents`.
 *   3. The "main" flow is: user does something → applyFilters() → fetch new
 *      HTML → renderResponse() swaps it in → re-init the small things that
 *      depend on the new HTML (sliders, accordion heights, product cards).
 * ============================================================================ */

class CollectionController {
  /* --------------------------------------------------------------------------
   * The constructor saves references to important DOM elements and creates
   * empty "state" properties that we'll fill in later. Don't put logic here —
   * just remember things. The actual setup happens in init().
   * -------------------------------------------------------------------------- */
  constructor(root) {
    // The outermost element of this collection section.
    this.root = root;

    // Read configuration from data-* attributes on the root element. These
    // come from Liquid (look at sections/main-collection.liquid).
    this.sectionId      = root.dataset.sectionId;
    this.collectionUrl  = root.dataset.collectionUrl;

    // Cache element references. Querying these once at startup is faster than
    // querying inside event handlers, and makes the rest of the code shorter.
    this.grid              = root.querySelector('[data-product-grid]');
    this.facetsContainer   = root.querySelector('#facets-container');
    this.form              = root.querySelector('[data-facet-form]');
    this.sortSelect        = root.querySelector('[data-sort-select]');
    this.progressBar       = root.querySelector('[data-progress-bar]');
    this.productCount      = root.querySelector('[data-product-count]');
    this.srStatus          = root.querySelector('[data-sr-status]');
    this.activeFilterCount = root.querySelector('[data-active-filter-count]');

    // STATE properties (filled in later as things happen)
    // ------------------------------------------------------------------------
    // Used to cancel a fetch if a newer one starts.
    this.currentFetch = null;

    // Timer IDs for debouncing. Holding these lets us cancel a pending fire.
    this.filterDebounceTimer = null;
    this.predictiveTimer     = null;

    // Are we currently fetching? Stops double-clicks.
    this.isLoading = false;

    // Cache of the slider's "true" min/max prices for the whole collection.
    // We capture this once on first load and reuse it forever — Shopify
    // re-computes filter.range_max based on filtered results which would
    // shrink the slider track. See initPriceSlider() for the full explanation.
    // Shape: { min: number, max: number } | null
    this.absolutePriceBounds = null;

    // Now do the actual setup
    this.init();
  }

  // ============================================================================
  // INITIAL SETUP
  // ============================================================================

  init() {
    this.bindEvents();
    this.initPriceSlider();
    this.initAccordions();
    this.updateActiveFilterBadge();
    this.initProductCards();

    // EDGE CASE: if the user landed on a URL that already has a price filter
    // (e.g. someone shared a link with ?filter.v.price.gte=50), then the
    // bounds we captured from the first render are actually the FILTERED
    // bounds, not the true collection range. Fetch the unfiltered collection
    // once in the background to get the real bounds.
    this.fetchTrueBoundsIfNeeded();
  }

  /* --------------------------------------------------------------------------
   * One central place where every event listener gets attached. Easier to find
   * "what triggers what" than searching the whole file.
   *
   * NOTE: We use event delegation (listening on `root`) instead of attaching
   * listeners directly to filters/buttons. Because we replace the facets HTML
   * on every AJAX call, direct listeners would be lost after each filter
   * change. Delegation survives because `root` itself never gets replaced.
   * -------------------------------------------------------------------------- */
  bindEvents() {
    // Listen for changes on any input inside the collection (filters, sort)
    this.root.addEventListener('change', (event) => {
      const target = event.target;

      if (target.matches('[data-facet-input]')) {
        this.onFilterChanged();
      } else if (target.matches('[data-price-input]')) {
        this.onPriceNumberChanged(event);
      } else if (target.matches('[data-sort-select]')) {
        this.applyFilters();
      }
    });

    // One delegated click handler routes to the right method based on what
    // was clicked. The data-* attribute on the button tells us what to do.
    this.root.addEventListener('click', (event) => this.onClick(event));

    // Browser back/forward button
    window.addEventListener('popstate', () => {
      // Re-render based on whatever URL the browser is now on, but DON'T
      // push a new history entry (the browser already moved us).
      this.applyFiltersFromUrl();
    });
  }

  // ============================================================================
  // CLICK ROUTER
  // ============================================================================

  /* --------------------------------------------------------------------------
   * Figures out which button was clicked (using data-* attributes) and calls
   * the matching method. We use `closest()` because users sometimes click on
   * an inner span/icon, not the button itself.
   * -------------------------------------------------------------------------- */
  onClick(event) {
    const button = event.target.closest('button, [data-accordion-trigger]');
    if (!button) return;

    // Action buttons (data-action="something")
    if (button.dataset.action === 'clear-all-filters') {
      event.preventDefault();
      this.clearAllFilters();
      return;
    }
    if (button.dataset.action === 'open-filters') {
      this.openMobileDrawer();
      return;
    }
    if (button.dataset.action === 'close-filters' ||
        button.dataset.action === 'apply-and-close') {
      this.closeMobileDrawer();
      return;
    }

    // Pill buttons that clear ONE filter
    if (button.hasAttribute('data-clear-filter')) {
      event.preventDefault();
      this.clearOneFilter(button);
      return;
    }

    // "Clear color" / "Clear size" etc. (the per-group clear link)
    if (button.hasAttribute('data-clear-group')) {
      event.preventDefault();
      this.clearFilterGroup(button);
      return;
    }

    // Load more button
    if (button.hasAttribute('data-load-more')) {
      event.preventDefault();
      this.loadMore(button);
      return;
    }

    // Accordion header (toggles a filter group open/closed)
    if (button.hasAttribute('data-accordion-trigger')) {
      const accordion = button.closest('.accordion');
      this.toggleAccordion(accordion);
      return;
    }
  }

  // ============================================================================
  // FILTER CHANGE HANDLERS
  // ============================================================================

  /* --------------------------------------------------------------------------
   * Called when the user clicks a checkbox or moves the slider. We DEBOUNCE
   * this — if the user clicks 5 boxes in 1 second we don't want 5 server
   * requests, we want one with all 5 selected. After 250ms of no new clicks,
   * we fire the request.
   * -------------------------------------------------------------------------- */
  onFilterChanged() {
    // Cancel any pending fire
    clearTimeout(this.filterDebounceTimer);

    // Schedule a new fire 250ms from now
    this.filterDebounceTimer = setTimeout(() => {
      this.applyFilters();
    }, 250);

    // Predictive count (the "Show 14 results" button) updates immediately
    // with its own separate, faster debounce.
    this.updatePredictiveCount();
  }

  /* --------------------------------------------------------------------------
   * When the user types a number in the price input fields, sync the slider
   * thumb to that value, then apply filters.
   * -------------------------------------------------------------------------- */
  onPriceNumberChanged(event) {
    const input = event.target;
    const slider = input.closest('[data-price-slider]');
    if (!slider) return;

    // Find the thumb (range input) on the same side (min or max)
    const side = input.dataset.priceInput; // "min" or "max"
    const thumb = slider.querySelector(`[data-price-thumb="${side}"]`);

    // Sync the slider thumb to match what the user typed
    if (thumb && input.value !== '') {
      thumb.value = input.value;
      this.updatePriceSliderFill(slider);
    }

    this.applyFilters();
  }

  // ============================================================================
  // CORE: APPLY FILTERS (the most important method)
  // ============================================================================

  /* --------------------------------------------------------------------------
   * The "main loop". This runs whenever filter/sort state changes:
   *   1. Build a URL with all active filters as query params
   *   2. Update the browser's URL bar (history.pushState)
   *   3. Fetch the new HTML for this section from Shopify
   *   4. Swap the new HTML into the page
   *
   * Called by every filter change handler in this class.
   * -------------------------------------------------------------------------- */
  applyFilters() {
    const params = this.serializeForm();
    const queryString = params.toString();

    // The full URL with collection path + filter params (used for fetching)
    let url = this.collectionUrl;
    if (queryString) {
      url = url + '?' + queryString;
    }

    // The URL we want in the browser bar (without the domain part)
    let browserUrl = window.location.pathname;
    if (queryString) {
      browserUrl = browserUrl + '?' + queryString;
    }

    // Update browser URL so back button works and the URL is shareable
    window.history.pushState({}, '', browserUrl);

    this.fetchAndRender(url);
  }

  /* Same as applyFilters() but reads the current URL instead of the form.
   * Used when the user clicks the browser back/forward buttons. */
  applyFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const queryString = params.toString();

    let url = this.collectionUrl;
    if (queryString) {
      url = url + '?' + queryString;
    }

    this.fetchAndRender(url);
  }

  /* --------------------------------------------------------------------------
   * Reads the form and builds a URLSearchParams object with all the active
   * filters. Skips empty values so the URL stays clean.
   * -------------------------------------------------------------------------- */
  serializeForm() {
    const params = new URLSearchParams();
    if (!this.form) return params;

    // Add every CHECKED checkbox
    const checkedBoxes = this.form.querySelectorAll('input[type="checkbox"]:checked');
    checkedBoxes.forEach(checkbox => {
      params.append(checkbox.name, checkbox.value);
    });

    // Add the price inputs IF the user actually set a value
    const priceInputs = this.form.querySelectorAll('[data-price-input]');
    priceInputs.forEach(input => {
      if (input.value === '' || input.value === null) return;

      const value = parseInt(input.value, 10);
      if (isNaN(value)) return;

      // Don't pollute the URL with min=0 or max=collectionMax — those values
      // mean "no filter on this side" and should be omitted.
      if (this.absolutePriceBounds) {
        const side = input.dataset.priceInput;
        if (side === 'min' && value <= this.absolutePriceBounds.min) return;
        if (side === 'max' && value >= this.absolutePriceBounds.max) return;
      }

      params.set(input.name, value);
    });

    // Add the sort dropdown value
    if (this.sortSelect && this.sortSelect.value) {
      params.set('sort_by', this.sortSelect.value);
    }

    return params;
  }

  // ============================================================================
  // FETCH + RENDER
  // ============================================================================

  /* --------------------------------------------------------------------------
   * Download new HTML for this section from Shopify and swap it into the page.
   *
   * The "?section_id=..." trick is Shopify's Section Rendering API. When you
   * add it to a URL, Shopify returns just the HTML for that one section
   * instead of a full page. Much faster.
   *
   * We also cancel any in-flight request before starting a new one — if the
   * user is clicking checkboxes quickly, we only care about the latest result.
   * -------------------------------------------------------------------------- */
  async fetchAndRender(url) {
    // Cancel any previous in-flight request
    if (this.currentFetch) {
      this.currentFetch.abort();
    }
    this.currentFetch = new AbortController();

    // Add the section_id query param to tell Shopify what to render
    const separator = url.includes('?') ? '&' : '?';
    const fetchUrl = url + separator + 'section_id=' + this.sectionId;

    this.showLoading(true);

    try {
      const response = await fetch(fetchUrl, {
        signal: this.currentFetch.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        throw new Error('Server returned status ' + response.status);
      }

      const html = await response.text();
      this.renderResponse(html);
      this.announceToScreenReader(this.productCount.textContent);
    } catch (error) {
      // AbortError is expected when we cancel — not a real error
      if (error.name === 'AbortError') return;
      console.error('[Collection] Fetch failed:', error);
      this.announceToScreenReader('Could not update products. Please try again.');
    } finally {
      this.showLoading(false);
    }
  }

  /* --------------------------------------------------------------------------
   * Take the new HTML returned by Shopify and surgically swap pieces of it
   * into the existing page. This is faster than a full page replace because
   * we don't lose the user's scroll position, the toolbar stays put, etc.
   * -------------------------------------------------------------------------- */
  renderResponse(html) {
    // Parse the HTML string into a real DOM document we can query
    const newDocument = new DOMParser().parseFromString(html, 'text/html');

    // Remember which accordions were OPEN so we can restore them after the
    // swap. We identify them by their label text.
    const openAccordionLabels = this.getOpenAccordionLabels();

    // 1. Swap the product grid
    const newGrid = newDocument.querySelector('[data-product-grid]');
    if (newGrid && this.grid) {
      this.grid.innerHTML = newGrid.innerHTML;
      this.grid.setAttribute('aria-busy', 'false');
    }

    // 2. Swap the facets sidebar
    const newFacets = newDocument.querySelector('#facets-container');
    if (newFacets && this.facetsContainer) {
      this.facetsContainer.innerHTML = newFacets.innerHTML;

      // The form element just got replaced too — re-cache the reference
      this.form = this.root.querySelector('[data-facet-form]');

      // Restore open/closed state of each accordion
      this.restoreOpenAccordions(openAccordionLabels);

      // Re-init the price slider (the slider HTML just got replaced)
      this.initPriceSlider();
    }

    // 3. Update the "X results" counter in the toolbar
    const newCount = newDocument.querySelector('[data-product-count]');
    if (newCount && this.productCount) {
      this.productCount.textContent = newCount.textContent;
    }

    // 4. Update the small badge on the "Filters" button (e.g. "2")
    this.updateActiveFilterBadge();

    // 5. Re-attach the Add To Cart handlers to the new product cards
    this.initProductCards();
  }

  // ============================================================================
  // CLEAR FILTER ACTIONS
  // ============================================================================

  /* --------------------------------------------------------------------------
   * "Clear all filters" button.
   * Strategy: navigate to the bare collection URL with no query params at all.
   * Cleaner than unchecking boxes one by one — we just throw the URL away.
   * -------------------------------------------------------------------------- */
  clearAllFilters() {
    // Update browser URL to the bare collection path
    window.history.pushState({}, '', window.location.pathname);

    // Fetch and render the unfiltered collection
    this.fetchAndRender(this.collectionUrl);
  }

  /* --------------------------------------------------------------------------
   * One of the small "X" pills above the filters that removes a single filter.
   * -------------------------------------------------------------------------- */
  clearOneFilter(pillButton) {
    const isPrice = pillButton.dataset.clearPrice === 'true';

    if (isPrice) {
      this.clearPriceFilter();
    } else {
      // Find the matching checkbox and uncheck it
      const param = pillButton.dataset.filterParam;
      const value = pillButton.dataset.filterValue;
      const selector = `input[name="${CSS.escape(param)}"][value="${CSS.escape(value)}"]`;
      const checkbox = this.form.querySelector(selector);
      if (checkbox) {
        checkbox.checked = false;
      }
    }

    this.applyFilters();
  }

  /* --------------------------------------------------------------------------
   * "Clear color" / "Clear size" — clears all values within ONE filter group.
   * -------------------------------------------------------------------------- */
  clearFilterGroup(clearButton) {
    const group = clearButton.closest('[data-facet-group]');
    if (!group) return;

    // Uncheck every checkbox in this group
    group.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = false;
    });

    // Reset any price inputs/sliders in this group
    group.querySelectorAll('[data-price-input]').forEach(input => {
      input.value = '';
    });
    group.querySelectorAll('[data-price-slider]').forEach(slider => {
      this.resetSliderToBounds(slider);
    });

    this.applyFilters();
  }

  /* Helper: clears the price filter specifically. */
  clearPriceFilter() {
    // Empty all price number inputs
    this.form.querySelectorAll('[data-price-input]').forEach(input => {
      input.value = '';
    });

    // Reset slider thumbs to their absolute bounds
    this.root.querySelectorAll('[data-price-slider]').forEach(slider => {
      this.resetSliderToBounds(slider);
    });
  }

  /* Helper: moves both slider thumbs back to their absolute min and max. */
  resetSliderToBounds(slider) {
    const minThumb = slider.querySelector('[data-price-thumb="min"]');
    const maxThumb = slider.querySelector('[data-price-thumb="max"]');
    if (minThumb) minThumb.value = minThumb.min;
    if (maxThumb) maxThumb.value = maxThumb.max;
    this.updatePriceSliderFill(slider);
  }

  // ============================================================================
  // ACTIVE FILTER BADGE
  // ============================================================================

  /* --------------------------------------------------------------------------
   * Updates the small number badge on the "Filters" button.
   * Counts: how many checkboxes are checked + 1 if a price filter is active.
   * -------------------------------------------------------------------------- */
  updateActiveFilterBadge() {
    if (!this.activeFilterCount || !this.form) return;

    // Count checked checkboxes
    const checkedCount = this.form.querySelectorAll('input[type="checkbox"]:checked').length;

    // Is the price filter active? (any price input has a value)
    let priceIsActive = false;
    this.form.querySelectorAll('[data-price-input]').forEach(input => {
      if (input.value !== '') priceIsActive = true;
    });

    const total = checkedCount + (priceIsActive ? 1 : 0);

    if (total > 0) {
      this.activeFilterCount.textContent = total;
      this.activeFilterCount.hidden = false;
    } else {
      this.activeFilterCount.textContent = '';
      this.activeFilterCount.hidden = true;
    }
  }

  // ============================================================================
  // PREDICTIVE COUNT (the "Show 14 results" button)
  // ============================================================================

  /* --------------------------------------------------------------------------
   * Sneak-peek at how many results the current filter selection would give,
   * BEFORE the user releases the slider or commits the change. Updates the
   * "Show N results" button at the bottom of the mobile drawer.
   *
   * It does this by firing a quick fetch with the current (possibly
   * mid-interaction) filter state, parsing out just the count, and ignoring
   * the rest. Best-effort — if it fails, no big deal.
   * -------------------------------------------------------------------------- */
  updatePredictiveCount() {
    clearTimeout(this.predictiveTimer);

    this.predictiveTimer = setTimeout(async () => {
      const params = this.serializeForm();
      const url = this.collectionUrl + '?' + params.toString() + '&section_id=' + this.sectionId;

      try {
        const response = await fetch(url, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const countEl = doc.querySelector('[data-product-count]');
        const buttonLabel = this.root.querySelector('[data-predictive-results]');

        if (countEl && buttonLabel) {
          // Pull just the number out of "12 results" → "12"
          const match = countEl.textContent.match(/\d+/);
          const number = match ? match[0] : '0';
          buttonLabel.textContent = 'Show ' + number + ' results';
        }
      } catch (e) {
        // Swallow errors silently — predictive count is a "nice to have"
      }
    }, 200);
  }

  // ============================================================================
  // PRICE SLIDER (two-thumb)
  // ============================================================================

  /* --------------------------------------------------------------------------
   * The price slider has two HTML range inputs stacked on top of each other,
   * one for min and one for max. We want:
   *   - The track always spans the collection's true min ($0) to true max.
   *   - The two thumbs slide along that track.
   *   - The thumbs can't cross each other.
   *   - Number inputs below stay in sync with the thumbs.
   *
   * KEY INSIGHT: Shopify's filter.range_max changes after every filter
   * (it returns the highest price WITHIN the filtered results, not the
   * collection's overall max). If we trusted those values, the slider track
   * would shrink every time the user filters. Instead, we capture the bounds
   * ONCE on first render, then enforce them on every subsequent re-render.
   * -------------------------------------------------------------------------- */
  initPriceSlider() {
    const sliders = this.root.querySelectorAll('[data-price-slider]');
    sliders.forEach(slider => this.setupOneSlider(slider));
  }

  setupOneSlider(slider) {
    const minThumb = slider.querySelector('[data-price-thumb="min"]');
    const maxThumb = slider.querySelector('[data-price-thumb="max"]');
    const minInput = slider.querySelector('[data-price-input="min"]');
    const maxInput = slider.querySelector('[data-price-input="max"]');

    if (!minThumb || !maxThumb) return;

    // STEP 1: Capture the absolute bounds (only on first call ever)
    if (this.absolutePriceBounds === null) {
      const min = parseInt(slider.dataset.min, 10);
      const max = parseInt(slider.dataset.max, 10);
      if (!isNaN(min) && !isNaN(max) && max > min) {
        this.absolutePriceBounds = { min: min, max: max };
      }
    }
    if (!this.absolutePriceBounds) return;

    const absMin = this.absolutePriceBounds.min;
    const absMax = this.absolutePriceBounds.max;

    // STEP 2: Read the current filter values from the URL.
    //   The URL is the source of truth (NOT the re-rendered HTML, which
    //   has filtered subset values that would corrupt our slider).
    const url = new URLSearchParams(window.location.search);
    const urlMin = url.get('filter.v.price.gte');
    const urlMax = url.get('filter.v.price.lte');

    // What position should the thumbs be at right now?
    let thumbMinValue = absMin;
    if (urlMin !== null) {
      thumbMinValue = parseInt(urlMin, 10);
    }

    let thumbMaxValue = absMax;
    if (urlMax !== null) {
      thumbMaxValue = parseInt(urlMax, 10);
    }

    // Safety: thumb values must be within bounds and not crossed
    if (thumbMinValue < absMin) thumbMinValue = absMin;
    if (thumbMaxValue > absMax) thumbMaxValue = absMax;
    if (thumbMinValue >= thumbMaxValue) thumbMinValue = thumbMaxValue - 1;

    // STEP 3: Apply bounds to the inputs.
    //   ORDER MATTERS: set min/max attributes BEFORE setting value.
    //   Otherwise the browser clamps the value to the OLD max attribute.
    minThumb.min = absMin;
    minThumb.max = absMax;
    maxThumb.min = absMin;
    maxThumb.max = absMax;

    minThumb.value = thumbMinValue;
    maxThumb.value = thumbMaxValue;

    // STEP 4: Update the number input fields below the slider.
    //   Empty if no filter is active on that side.
    if (minInput) {
      minInput.min = absMin;
      minInput.max = absMax;
      minInput.placeholder = absMin;
      minInput.value = (urlMin !== null) ? thumbMinValue : '';
    }
    if (maxInput) {
      maxInput.min = absMin;
      maxInput.max = absMax;
      maxInput.placeholder = absMax;
      maxInput.value = (urlMax !== null) ? thumbMaxValue : '';
    }

    // Update the visual fill bar between the thumbs
    this.updatePriceSliderFill(slider);

    // STEP 5: Wire up event listeners (only ONCE per slider element).
    //   We use a data attribute as a flag so we don't double-bind listeners
    //   if this method runs twice on the same element.
    if (slider.dataset.listenersAttached === 'true') return;
    slider.dataset.listenersAttached = 'true';

    this.attachSliderListeners(slider, minThumb, maxThumb, minInput, maxInput);
  }

  /* --------------------------------------------------------------------------
   * Attach drag listeners to the two thumbs.
   * - `input` event fires continuously while dragging
   * - `change` event fires once when the user releases
   * - `pointerdown` is used to raise z-index of the thumb being grabbed,
   *   so the user can grab either thumb even when they overlap.
   * -------------------------------------------------------------------------- */
  attachSliderListeners(slider, minThumb, maxThumb, minInput, maxInput) {

    // Helper: keep the two thumbs from crossing each other
    const stopThumbsFromCrossing = (whichOneJustMoved) => {
      const minValue = parseInt(minThumb.value, 10);
      const maxValue = parseInt(maxThumb.value, 10);

      if (whichOneJustMoved === 'min' && minValue >= maxValue) {
        minThumb.value = maxValue - 1;
      }
      if (whichOneJustMoved === 'max' && maxValue <= minValue) {
        maxThumb.value = minValue + 1;
      }
    };

    // Helper: copy slider thumb values into the number input fields
    const syncNumberInputs = () => {
      if (minInput) minInput.value = minThumb.value;
      if (maxInput) maxInput.value = maxThumb.value;
    };

    // ----- MIN THUMB -----
    minThumb.addEventListener('pointerdown', () => {
      // Bring the thumb the user is grabbing to the front
      minThumb.style.zIndex = '3';
      maxThumb.style.zIndex = '2';
    });

    minThumb.addEventListener('input', () => {
      stopThumbsFromCrossing('min');
      syncNumberInputs();
      this.updatePriceSliderFill(slider);
      this.updatePredictiveCount();
    });

    minThumb.addEventListener('change', () => {
      this.applyFilters();
    });

    // ----- MAX THUMB -----
    maxThumb.addEventListener('pointerdown', () => {
      maxThumb.style.zIndex = '3';
      minThumb.style.zIndex = '2';
    });

    maxThumb.addEventListener('input', () => {
      stopThumbsFromCrossing('max');
      syncNumberInputs();
      this.updatePriceSliderFill(slider);
      this.updatePredictiveCount();
    });

    maxThumb.addEventListener('change', () => {
      this.applyFilters();
    });
  }

  /* --------------------------------------------------------------------------
   * Redraws the colored bar BETWEEN the two thumbs. We compute this in
   * percent (not pixels) so it works at any width.
   * -------------------------------------------------------------------------- */
  updatePriceSliderFill(slider) {
    const minThumb = slider.querySelector('[data-price-thumb="min"]');
    const maxThumb = slider.querySelector('[data-price-thumb="max"]');
    const fill     = slider.querySelector('[data-price-fill]');
    if (!minThumb || !maxThumb || !fill) return;

    const trackMin = parseInt(minThumb.min, 10);
    const trackMax = parseInt(minThumb.max, 10);
    const trackRange = trackMax - trackMin;
    if (trackRange <= 0) return;

    const minValue = parseInt(minThumb.value, 10);
    const maxValue = parseInt(maxThumb.value, 10);

    // Convert the values into "% of the track from the left"
    const leftPercent  = ((minValue - trackMin) / trackRange) * 100;
    const rightPercent = ((maxValue - trackMin) / trackRange) * 100;

    fill.style.left  = leftPercent + '%';
    fill.style.width = (rightPercent - leftPercent) + '%';
  }

  /* --------------------------------------------------------------------------
   * Background fetch to get the TRUE collection price bounds when the user
   * arrives with a price filter already in the URL.
   * -------------------------------------------------------------------------- */
  async fetchTrueBoundsIfNeeded() {
    const params = new URLSearchParams(window.location.search);
    const hasPriceFilter = params.has('filter.v.price.gte') ||
                           params.has('filter.v.price.lte');

    // No price filter? The bounds we captured are already correct.
    if (!hasPriceFilter) return;

    try {
      const url = this.collectionUrl + '?section_id=' + this.sectionId;
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const slider = doc.querySelector('[data-price-slider]');
      if (!slider) return;

      const trueMin = parseInt(slider.dataset.min, 10);
      const trueMax = parseInt(slider.dataset.max, 10);
      if (isNaN(trueMin) || isNaN(trueMax) || trueMax <= trueMin) return;

      // Overwrite cached bounds and redraw the slider
      this.absolutePriceBounds = { min: trueMin, max: trueMax };
      this.initPriceSlider();
    } catch (e) {
      // Best-effort — ignore failures
    }
  }

  // ============================================================================
  // ACCORDION (slide animation, like jQuery slideToggle)
  // ============================================================================

  /* --------------------------------------------------------------------------
   * Why we animate `max-height` instead of `height`:
   * CSS can't transition `height: auto`. We need a known pixel value at
   * both ends. We measure the content's natural height with `scrollHeight`
   * and animate from 0 → that. CSS handles the easing.
   * -------------------------------------------------------------------------- */
  initAccordions() {
    // Set initial pixel height for accordions that start open. Without this,
    // they'd be closed because CSS defaults their max-height to 0.
    this.openAccordionsThatStartOpen();

    // If the window resizes, an open accordion's content might reflow to a
    // different height. Recalculate.
    window.addEventListener('resize', () => {
      this.recalculateOpenAccordionHeights();
    });
  }

  openAccordionsThatStartOpen() {
    const openAccordions = this.root.querySelectorAll('.accordion.accordion--active');
    openAccordions.forEach(accordion => {
      const content = accordion.querySelector('[data-accordion-content]');
      if (content) {
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    });
  }

  recalculateOpenAccordionHeights() {
    const openAccordions = this.root.querySelectorAll('.accordion.accordion--active');
    openAccordions.forEach(accordion => {
      const content = accordion.querySelector('[data-accordion-content]');
      if (content) {
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    });
  }

  /* --------------------------------------------------------------------------
   * Open or close an accordion when its header is clicked.
   * -------------------------------------------------------------------------- */
  toggleAccordion(accordion) {
    if (!accordion) return;

    const content = accordion.querySelector('[data-accordion-content]');
    if (!content) return;

    const isOpen = accordion.classList.contains('accordion--active');

    if (isOpen) {
      this.closeAccordion(accordion, content);
    } else {
      this.openAccordion(accordion, content);
    }
  }

  openAccordion(accordion, content) {
    accordion.classList.add('accordion--active');
    // Animate from 0 (current) → scrollHeight (target)
    content.style.maxHeight = content.scrollHeight + 'px';
  }

  closeAccordion(accordion, content) {
    // To animate from a value → 0, we first need a fixed starting value.
    // (We can't transition from "auto" or unset.) So set it to the current
    // measured height, force the browser to apply that, then set it to 0.
    content.style.maxHeight = content.scrollHeight + 'px';

    // This line forces the browser to apply the line above before we change
    // it again. Without this, the browser collapses both changes into one
    // and you see no animation.
    content.offsetHeight; // eslint-disable-line no-unused-expressions

    content.style.maxHeight = '0px';
    accordion.classList.remove('accordion--active');
  }

  /* Helpers used by renderResponse() to remember/restore which accordions
   * were open before the AJAX swap. */
  getOpenAccordionLabels() {
    const labels = [];
    const openOnes = this.facetsContainer.querySelectorAll('.accordion.accordion--active');
    openOnes.forEach(accordion => {
      const labelEl = accordion.querySelector('.facet-group__label');
      if (labelEl) labels.push(labelEl.textContent.trim());
    });
    return labels;
  }

  restoreOpenAccordions(labelsToOpen) {
    const accordions = this.facetsContainer.querySelectorAll('.accordion');
    accordions.forEach(accordion => {
      const labelEl = accordion.querySelector('.facet-group__label');
      const label = labelEl ? labelEl.textContent.trim() : '';
      const content = accordion.querySelector('[data-accordion-content]');

      if (labelsToOpen.includes(label)) {
        accordion.classList.add('accordion--active');
        if (content) content.style.maxHeight = content.scrollHeight + 'px';
      } else {
        accordion.classList.remove('accordion--active');
        if (content) content.style.maxHeight = '0px';
      }
    });
  }

  // ============================================================================
  // LOAD MORE (pagination)
  // ============================================================================

  /* --------------------------------------------------------------------------
   * Loads the next page of products and APPENDS them to the grid.
   * Different from filter changes — those REPLACE the grid.
   * -------------------------------------------------------------------------- */
  async loadMore(button) {
    const nextPageUrl = button.dataset.nextUrl;
    if (!nextPageUrl || this.isLoading) return;

    // Save scroll position so we can restore it after appending (some
    // browsers nudge the scroll on DOM change)
    const savedScrollY = window.scrollY;

    button.classList.add('is-loading');
    button.disabled = true;

    try {
      // Fetch the next page
      const separator = nextPageUrl.includes('?') ? '&' : '?';
      const fetchUrl = nextPageUrl + separator + 'section_id=' + this.sectionId;

      const response = await fetch(fetchUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) {
        throw new Error('Server returned status ' + response.status);
      }

      const html = await response.text();
      const newDocument = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = newDocument.querySelector('[data-product-grid]');
      if (!newGrid) throw new Error('No product grid in response');

      // Find the new product cards and the load-more wrapper to insert before
      const newCards = newGrid.querySelectorAll('.product-card');
      const oldWrapper = button.closest('[data-load-more-wrapper]');

      // Append new cards (insert each one before the load-more wrapper)
      newCards.forEach(card => {
        this.grid.insertBefore(card, oldWrapper);
      });

      // Replace the load-more wrapper itself (might be a new "next page" URL,
      // or it might be gone entirely if we just loaded the last page)
      const newWrapper = newGrid.querySelector('[data-load-more-wrapper]');
      if (newWrapper) {
        oldWrapper.replaceWith(newWrapper);
        // The new wrapper's "Showing X of Y" text is for THIS PAGE only.
        // Override it with the cumulative count.
        this.updateLoadMoreStatus();
      } else {
        oldWrapper.remove();
      }

      // Restore scroll position
      window.scrollTo({ top: savedScrollY, behavior: 'instant' });

      // Wire up Add To Cart on the new cards
      this.initProductCards();

      this.announceToScreenReader('Loaded ' + newCards.length + ' more products');
    } catch (error) {
      console.error('[Collection] Load more failed:', error);
      button.classList.remove('is-loading');
      button.disabled = false;
      this.announceToScreenReader('Could not load more products.');
    }
  }

  /* --------------------------------------------------------------------------
   * Update the "Showing X of Y" text under the Load More button to reflect
   * the actual cumulative card count, since Shopify's Liquid only knows
   * about the current page.
   * -------------------------------------------------------------------------- */
  updateLoadMoreStatus() {
    const statusEl = this.grid.querySelector('.load-more-status');
    if (!statusEl) return;

    // Count how many cards are currently visible in the grid
    const visibleCount = this.grid.querySelectorAll('.product-card').length;

    // Get the total from the toolbar (which is always the unfiltered or
    // filtered total, kept fresh by every AJAX response)
    let total = null;
    if (this.productCount) {
      const match = this.productCount.textContent.match(/\d+/);
      if (match) total = parseInt(match[0], 10);
    }

    if (total !== null) {
      statusEl.textContent = 'Showing ' + visibleCount + ' of ' + total;
    } else {
      statusEl.textContent = 'Showing ' + visibleCount;
    }
  }

  // ============================================================================
  // PRODUCT CARDS (re-init the user's existing ATC code)
  // ============================================================================

  /* --------------------------------------------------------------------------
   * The product card snippet has its own Add To Cart code (in product-card.js)
   * that registers a global helper at `window.ProductCard.init(rootElement)`.
   *
   * Whenever we replace cards (filter change OR load more), we re-call that
   * init function on the new cards. The user's code uses an idempotency flag
   * so already-bound forms are skipped.
   * -------------------------------------------------------------------------- */
  initProductCards() {
    if (window.ProductCard && typeof window.ProductCard.init === 'function') {
      window.ProductCard.init(this.grid);
      return;
    }

    // RACE CONDITION GUARD: if our script ran before product-card.js, retry
    // once after window.load (which fires after all scripts are parsed).
    if (this.productCardRetryScheduled) return;
    this.productCardRetryScheduled = true;

    window.addEventListener('load', () => {
      if (window.ProductCard && typeof window.ProductCard.init === 'function') {
        window.ProductCard.init(this.grid);
      }
    }, { once: true });
  }

  // ============================================================================
  // LOADING STATES
  // ============================================================================

  showLoading(isLoading) {
    this.isLoading = isLoading;

    // Set aria-busy so screen readers know content is updating
    if (this.grid) {
      this.grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    // Show/hide the progress bar
    if (this.progressBar) {
      if (isLoading) {
        this.progressBar.classList.add('is-active');
      } else {
        this.progressBar.classList.remove('is-active');
      }
    }

    // Dim the grid during load
    if (this.grid) {
      if (isLoading) {
        this.grid.classList.add('is-loading');
      } else {
        this.grid.classList.remove('is-loading');
      }
    }
  }

  // ============================================================================
  // MOBILE FILTER DRAWER
  // ============================================================================

  openMobileDrawer() {
    document.body.classList.add('facets-drawer-open');

    if (this.facetsContainer) {
      this.facetsContainer.classList.add('is-open');
      this.facetsContainer.setAttribute('aria-hidden', 'false');
    }

    const trigger = this.root.querySelector('[data-action="open-filters"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    // Recalculate accordion heights — they might have been measured at a
    // different width before the drawer opened.
    this.recalculateOpenAccordionHeights();

    // Move keyboard focus into the drawer for accessibility
    const firstFocusable = this.facetsContainer.querySelector(
      'button, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) firstFocusable.focus();
  }

  closeMobileDrawer() {
    document.body.classList.remove('facets-drawer-open');

    if (this.facetsContainer) {
      this.facetsContainer.classList.remove('is-open');
      this.facetsContainer.setAttribute('aria-hidden', 'true');
    }

    const trigger = this.root.querySelector('[data-action="open-filters"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }
  }

  // ============================================================================
  // SCREEN READER ANNOUNCEMENTS
  // ============================================================================

  /* --------------------------------------------------------------------------
   * Update an aria-live region so screen readers announce changes.
   * The 50ms delay + clear is a workaround — some screen readers won't
   * re-read the same text twice unless the content actually changes.
   * -------------------------------------------------------------------------- */
  announceToScreenReader(message) {
    if (!this.srStatus) return;

    this.srStatus.textContent = '';
    setTimeout(() => {
      this.srStatus.textContent = message;
    }, 50);
  }
}

/* ============================================================================
 * BOOTSTRAP — find every collection section on the page and start one
 * controller per section.
 * ============================================================================ */

function startAllCollections() {
  const sections = document.querySelectorAll('[data-section-type="main-collection"]');

  sections.forEach(section => {
    // Don't double-init if this section has already been started
    if (section.collectionControllerInstance) return;

    section.collectionControllerInstance = new CollectionController(section);
  });
}

// Run as soon as the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startAllCollections);
} else {
  startAllCollections();
}

// Re-run inside the Shopify theme editor when a section is reloaded
document.addEventListener('shopify:section:load', startAllCollections);
